/** Small shared presentational atoms for the Brisque AI surface. */
import React from 'react';
import { Channel, CHANNEL_LABELS } from '../data/types';

export const FONT = 'var(--font-primary)';

/** Render text with **bold** spans. */
export const Rich: React.FC<{ text: string }> = ({ text }) => {
  const parts = (text ?? '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <strong key={i} style={{ fontWeight: 700, color: 'var(--t1)' }}>{p.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
};

export const Icon: React.FC<{ name: string; size?: number; color?: string; style?: React.CSSProperties }> = ({ name, size = 18, color = 'inherit', style }) => (
  <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, color, userSelect: 'none', flexShrink: 0, ...style }}>{name}</span>
);

export const channelLabel = (c: Channel): string => CHANNEL_LABELS[c];

export const Chip: React.FC<{
  label: string;
  active?: boolean;
  onClick?: () => void;
  /** `success` = an already-satisfied channel: soft green fill, never the accent red. */
  tone?: 'accent' | 'neutral' | 'success';
  icon?: string;
  disabled?: boolean;
  small?: boolean;
}> = ({ label, active, onClick, tone = 'accent', icon, disabled, small }) => {
  const success = tone === 'success';
  const activeBg = tone === 'neutral' ? 'var(--t1)' : 'var(--accent)';
  // Success chips stay legible at full strength — they read as "done", not as an inert control.
  const border = success ? 'rgba(22,163,74,0.30)' : active ? 'transparent' : 'var(--card-border)';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: small ? '3px 8px' : '5px 11px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: success ? 'rgba(22,163,74,0.12)' : active ? activeBg : 'var(--surface)',
        color: success ? 'var(--green)' : active ? '#fff' : 'var(--t2)',
        fontSize: small ? 11 : 12, fontWeight: 600, fontFamily: FONT,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled && !success ? 0.5 : 1,
        transition: 'all .15s', whiteSpace: 'nowrap',
      }}
    >
      {icon && <Icon name={icon} size={small ? 13 : 14} />}
      {label}
    </button>
  );
};

export const PrimaryBtn: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean; icon?: string }> = ({ children, onClick, disabled, icon }) => (
  <button
    type="button" onClick={onClick} disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '9px 16px', borderRadius: 'var(--radius-button)', border: 'none',
      background: disabled ? 'var(--card-border)' : 'var(--accent)', color: '#fff',
      fontSize: 13, fontWeight: 700, fontFamily: FONT, cursor: disabled ? 'default' : 'pointer',
      boxShadow: disabled ? 'none' : '0 2px 8px rgba(210,58,59,0.25)', transition: 'all .15s',
    }}
  >
    {icon && <Icon name={icon} size={16} />}
    {children}
  </button>
);

export const GhostBtn: React.FC<{ children: React.ReactNode; onClick?: () => void; danger?: boolean; icon?: string; small?: boolean }> = ({ children, onClick, danger, icon, small }) => (
  <button
    type="button" onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: small ? '5px 10px' : '9px 14px', borderRadius: 'var(--radius-button)',
      border: `1px solid var(--card-border)`, background: 'var(--surface)',
      color: danger ? 'var(--red)' : 'var(--t2)', fontSize: small ? 12 : 13, fontWeight: 600,
      fontFamily: FONT, cursor: 'pointer', transition: 'all .15s',
    }}
  >
    {icon && <Icon name={icon} size={small ? 14 : 16} />}
    {children}
  </button>
);

export const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'info'; children: React.ReactNode }> = ({ tone, children }) => {
  const map = {
    ok: { c: 'var(--green)', b: 'rgba(22,163,74,0.10)' },
    warn: { c: 'var(--orange)', b: 'rgba(217,119,6,0.10)' },
    bad: { c: 'var(--red)', b: 'rgba(220,38,38,0.10)' },
    info: { c: 'var(--t2)', b: 'var(--bg)' },
  }[tone];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: map.c, background: map.b, fontFamily: FONT }}>
      {children}
    </span>
  );
};
